import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getUserToken } from 'src/shared/utils/session'

export const teamsApi = createApi({
    reducerPath: 'teamsApi',
    baseQuery: fetchBaseQuery({ 
        baseUrl: '/api/v2/storefront',
        prepareHeaders: (headers, { getState }) => {
            const token = getUserToken();
            if (token) {
                headers.set('Authorization', `Bearer ${token}`);
            }
            return headers;
        }
    }),
    tagTypes: ['Teams', 'TeamMembers'],

    endpoints: (builder) => ({
        getTeams: builder.query({
            query: () => '/teams',
            providesTags: ['Teams'],
        }),

        getTeam: builder.query({
            query: (id) => `/teams/${id}`,
            providesTags: (result, error, id) => [{ type: 'Teams', id }],
        }),

        createTeam: builder.mutation({
            query: (body) => ({
                url: '/teams',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Teams'],
        }),

        updateTeam: builder.mutation({
            query: ({ id, ...body }) => ({
                url: `/teams/${id}`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['Teams'],
        }),

        deleteTeam: builder.mutation({
            query: (id) => ({
                url: `/teams/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Teams'],
        }),

        getTeamMembers: builder.query({
            query: (teamId) => `/teams/${teamId}/members`,
            providesTags: ['TeamMembers'],
        }),

        inviteMember: builder.mutation({
            query: ({ teamId, email, role }) => ({
                url: `/teams/${teamId}/invite_member`,
                method: 'POST',
                body: { email, role },
            }),
            invalidatesTags: ['TeamMembers'],
        }),

        updateMemberRole: builder.mutation({
            query: ({ teamId, userId, role }) => ({
                url: `/teams/${teamId}/update_member_role`,
                method: 'PATCH',
                body: { user_id: userId, role },
            }),
            invalidatesTags: ['TeamMembers'],
        }),

        removeMember: builder.mutation({
            query: ({ teamId, memberId }) => ({
                url: `/teams/${teamId}/members/${memberId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['TeamMembers'],
        }),
        acceptTeamInvite: builder.mutation({
            query: ({ token }) => ({
                url: `/teams/accept_invite`,
                method: 'POST',
                body: { token },
            }),
        }),
        getTeamWalletLogs: builder.query({
            query: ({ teamId, page = 1, sort = 'desc' }) =>
                `/teams/${teamId}/wallet_transactions?page=${page}&sort=${sort}&per_page=10`,
        }),
        getTeamBoms: builder.query({
            query: ({ teamId, q, page }) => `teams/${teamId}/boms?${q}&page=${page}&per_page=10`,
        }),
    }),
});

export const {
  useGetTeamsQuery,
  useCreateTeamMutation,
  useUpdateTeamMutation,
  useDeleteTeamMutation,
  useGetTeamMembersQuery,
  useInviteMemberMutation,
  useRemoveMemberMutation,
  useAcceptTeamInviteMutation,
  useLazyGetTeamWalletLogsQuery,
  useLazyGetTeamBomsQuery,
  useUpdateMemberRoleMutation
} = teamsApi;
